import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  LocalShipping as ShippingIcon,
  Inventory as InventoryIcon,
  TrendingDown as TrendingDownIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import {
  getFbaDiscrepancySummary,
  getFbaDiscrepancyShipments,
  getFbaShipmentDetails,
  getFbaDiscrepancyBrands,
  uploadFbaShipmentCSV,
  previewFbaShipmentCSV,
  getFbaShipmentCSVStats,
  exportFbaDiscrepancies,
  formatNumber
} from '../../services/inventoryService';

function FbaShipmentDiscrepancies() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Summary data
  const [summary, setSummary] = useState(null);

  // Shipments list
  const [shipments, setShipments] = useState([]);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 0,
    rowsPerPage: 25
  });

  // Filters
  const [brands, setBrands] = useState([]);
  const [filters, setFilters] = useState({
    brand: 'all',
    sku: '',
    shipment_id: '',
    min_missing: ''
  });

  // Sorting
  const [orderBy, setOrderBy] = useState('missing');
  const [order, setOrder] = useState('desc');

  // Detail dialog
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [shipmentDetail, setShipmentDetail] = useState(null);

  // CSV Upload dialog
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvStats, setCsvStats] = useState(null);
  const [csvMessage, setCsvMessage] = useState(null);

  // Load initial data
  useEffect(() => {
    loadData();
    loadBrands();
  }, []);

  // Reload shipments when filters or pagination change
  useEffect(() => {
    loadShipments();
  }, [filters.brand, pagination.page, pagination.rowsPerPage, orderBy, order]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        loadSummary(),
        loadShipments()
      ]);
    } catch (err) {
      console.error('Error loading FBA discrepancies:', err);
      setError('Failed to load FBA discrepancy data');
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await getFbaDiscrepancySummary();
      if (response.success) {
        setSummary(response.data);
      }
    } catch (err) {
      console.error('Error loading summary:', err);
    }
  };

  const loadBrands = async () => {
    try {
      const response = await getFbaDiscrepancyBrands();
      if (response.success) {
        setBrands(response.data.brands || []);
      }
    } catch (err) {
      console.error('Error loading brands:', err);
    }
  };

  const loadShipments = useCallback(async () => {
    setShipmentsLoading(true);
    try {
      const params = {
        brand: filters.brand !== 'all' ? filters.brand : undefined,
        sku: filters.sku || undefined,
        shipment_id: filters.shipment_id || undefined,
        min_missing: filters.min_missing || undefined,
        sort_by: orderBy,
        sort_order: order.toUpperCase(),
        limit: pagination.rowsPerPage,
        offset: pagination.page * pagination.rowsPerPage
      };

      console.log('Loading shipments with params:', params);
      const response = await getFbaDiscrepancyShipments(params);
      console.log('Shipments response:', response);
      if (response.success) {
        setShipments(response.data.shipments || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0
        }));
      } else {
        console.error('Shipments API returned error:', response);
        setShipments([]);
      }
    } catch (err) {
      console.error('Error loading shipments:', err);
      setShipments([]);
    } finally {
      setShipmentsLoading(false);
    }
  }, [filters, pagination.page, pagination.rowsPerPage, orderBy, order]);

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 0 }));
    loadShipments();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event) => {
    setPagination(prev => ({
      ...prev,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0
    }));
  };

  const handleViewShipment = async (shipmentId) => {
    setSelectedShipment(shipmentId);
    setDetailLoading(true);
    try {
      const response = await getFbaShipmentDetails(shipmentId);
      if (response.success) {
        setShipmentDetail(response.data);
      }
    } catch (err) {
      console.error('Error loading shipment details:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseDetail = () => {
    setSelectedShipment(null);
    setShipmentDetail(null);
  };

  // CSV Upload handlers
  const handleOpenCsvDialog = async () => {
    setCsvDialogOpen(true);
    setCsvFile(null);
    setCsvPreview(null);
    setCsvMessage(null);
    try {
      const response = await getFbaShipmentCSVStats();
      if (response.success) {
        setCsvStats(response.data);
      }
    } catch (err) {
      console.error('Error loading CSV stats:', err);
    }
  };

  const handleCloseCsvDialog = () => {
    setCsvDialogOpen(false);
    setCsvFile(null);
    setCsvPreview(null);
    setCsvMessage(null);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setCsvFile(file);
    setCsvMessage(null);
    setCsvUploading(true);

    try {
      const response = await previewFbaShipmentCSV(file);
      if (response.success) {
        setCsvPreview(response.data);
      } else {
        setCsvMessage({ type: 'error', text: response.error });
      }
    } catch (err) {
      console.error('Error previewing CSV:', err);
      setCsvMessage({ type: 'error', text: err.message || 'Failed to preview CSV file' });
    } finally {
      setCsvUploading(false);
    }
  };

  const handleUploadCsv = async () => {
    if (!csvFile) return;

    setCsvUploading(true);
    setCsvMessage(null);

    try {
      const response = await uploadFbaShipmentCSV(csvFile);
      if (response.success) {
        setCsvMessage({
          type: 'success',
          text: response.message || `Successfully uploaded ${response.data.insertedRows} rows for ${response.data.uniqueShipments} shipments`
        });
        setCsvFile(null);
        setCsvPreview(null);
        // Reload stats
        const statsResponse = await getFbaShipmentCSVStats();
        if (statsResponse.success) {
          setCsvStats(statsResponse.data);
        }
        // Reload main data to reflect new dates
        loadData();
      } else {
        setCsvMessage({ type: 'error', text: response.error });
      }
    } catch (err) {
      console.error('Error uploading CSV:', err);
      setCsvMessage({ type: 'error', text: err.message || 'Failed to upload CSV file' });
    } finally {
      setCsvUploading(false);
    }
  };

  // Export handler
  const handleExport = async () => {
    try {
      const exportParams = {
        brand: filters.brand !== 'all' ? filters.brand : undefined,
        sku: filters.sku || undefined,
        shipment_id: filters.shipment_id || undefined,
        min_missing: filters.min_missing || undefined
      };

      await exportFbaDiscrepancies(exportParams);
    } catch (err) {
      console.error('Error exporting discrepancies:', err);
      setError('Failed to export discrepancies. Please try again.');
    }
  };

  const getDiscrepancyColor = (pct) => {
    if (pct >= 50) return 'error';
    if (pct >= 20) return 'warning';
    return 'default';
  };

  // Summary card component
  const SummaryCard = ({ title, value, subtitle, icon, color = 'primary' }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography color="textSecondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div" fontWeight="bold">
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}.light`,
              borderRadius: 2,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {React.cloneElement(icon, { sx: { color: `${color}.main`, fontSize: 28 } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading && !shipments.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <WarningIcon sx={{ fontSize: 40, color: 'warning.main' }} />
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  FBA Shipment Discrepancies
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Closed shipments where received quantity differs from shipped quantity
                </Typography>
              </Box>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={handleOpenCsvDialog}
              >
                Upload CSV
              </Button>
              <Tooltip title="Refresh data">
                <IconButton onClick={loadData} color="primary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title="Shipments with Discrepancies"
              value={formatNumber(summary.totals?.total_shipments || 0)}
              subtitle={`${formatNumber(summary.totals?.total_line_items || 0)} line items`}
              icon={<ShippingIcon />}
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title="Total Units Shipped"
              value={formatNumber(summary.totals?.total_shipped || 0)}
              icon={<InventoryIcon />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title="Total Units Missing"
              value={formatNumber(summary.totals?.total_missing || 0)}
              subtitle={`${summary.totals?.missing_pct || 0}% of shipped`}
              icon={<TrendingDownIcon />}
              color="error"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title="Brands Affected"
              value={summary.byBrand?.length || 0}
              icon={<InfoIcon />}
              color="info"
            />
          </Grid>
        </Grid>
      )}

      {/* Brand Summary Table */}
      {summary?.byBrand && summary.byBrand.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Summary by Brand
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Brand</TableCell>
                    <TableCell align="right">Shipments</TableCell>
                    <TableCell align="right">Shipped</TableCell>
                    <TableCell align="right">Received</TableCell>
                    <TableCell align="right">Missing</TableCell>
                    <TableCell align="right">% Missing</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.byBrand.map((row) => (
                    <TableRow
                      key={row.brand}
                      hover
                      selected={filters.brand === row.brand}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: filters.brand === row.brand ? 'action.selected' : 'inherit'
                      }}
                      onClick={() => {
                        setFilters(prev => ({ ...prev, brand: row.brand }));
                        setPagination(prev => ({ ...prev, page: 0 }));
                      }}
                    >
                      <TableCell>
                        <Typography fontWeight="medium">{row.brand}</Typography>
                      </TableCell>
                      <TableCell align="right">{formatNumber(row.shipment_count)}</TableCell>
                      <TableCell align="right">{formatNumber(row.total_shipped)}</TableCell>
                      <TableCell align="right">{formatNumber(row.total_received)}</TableCell>
                      <TableCell align="right">
                        <Typography color="error.main" fontWeight="medium">
                          {formatNumber(row.total_missing)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${row.missing_pct}%`}
                          size="small"
                          color={getDiscrepancyColor(row.missing_pct)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Brand</InputLabel>
              <Select
                value={filters.brand}
                onChange={(e) => setFilters(prev => ({ ...prev, brand: e.target.value }))}
                label="Brand"
              >
                <MenuItem value="all">All Brands</MenuItem>
                {brands.filter(brand => String(brand).trim().toLowerCase() !== 'company level').map(brand => (
                  <MenuItem key={brand} value={brand}>{brand}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Shipment ID"
              value={filters.shipment_id}
              onChange={(e) => setFilters(prev => ({ ...prev, shipment_id: e.target.value }))}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{ minWidth: 200 }}
            />
            <TextField
              label="SKU"
              value={filters.sku}
              onChange={(e) => setFilters(prev => ({ ...prev, sku: e.target.value }))}
              onKeyPress={handleKeyPress}
              sx={{ minWidth: 200 }}
            />
            <TextField
              label="Min. Missing Units"
              type="number"
              value={filters.min_missing}
              onChange={(e) => setFilters(prev => ({ ...prev, min_missing: e.target.value }))}
              onKeyPress={handleKeyPress}
              sx={{ minWidth: 150 }}
            />
            <Button variant="contained" onClick={handleSearch}>
              Search
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
              color="primary"
            >
              Export
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Shipments Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Shipment Details
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'shipment_id'}
                      direction={orderBy === 'shipment_id' ? order : 'asc'}
                      onClick={() => handleSort('shipment_id')}
                    >
                      Shipment ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'brand'}
                      direction={orderBy === 'brand' ? order : 'asc'}
                      onClick={() => handleSort('brand')}
                    >
                      Brand
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">SKUs</TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'shipped'}
                      direction={orderBy === 'shipped' ? order : 'asc'}
                      onClick={() => handleSort('shipped')}
                    >
                      Shipped
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'received'}
                      direction={orderBy === 'received' ? order : 'asc'}
                      onClick={() => handleSort('received')}
                    >
                      Received
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'missing'}
                      direction={orderBy === 'missing' ? order : 'asc'}
                      onClick={() => handleSort('missing')}
                    >
                      Missing
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'missing_pct'}
                      direction={orderBy === 'missing_pct' ? order : 'asc'}
                      onClick={() => handleSort('missing_pct')}
                    >
                      % Missing
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'sort_date'}
                      direction={orderBy === 'sort_date' ? order : 'asc'}
                      onClick={() => handleSort('sort_date')}
                    >
                      Ship Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shipmentsLoading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Box sx={{ py: 3 }}>
                        <CircularProgress size={24} />
                        <Typography color="textSecondary" sx={{ mt: 1 }}>
                          Loading shipments...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : shipments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography color="textSecondary" sx={{ py: 3 }}>
                        No shipments with discrepancies found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  shipments.map((shipment) => (
                    <TableRow key={shipment.shipment_id} hover>
                      <TableCell>
                        <Typography fontWeight="medium" sx={{ fontFamily: 'monospace' }}>
                          {shipment.shipment_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={shipment.shipment_name || ''}>
                          <Typography noWrap sx={{ maxWidth: 200 }}>
                            {shipment.shipment_name || '—'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip label={shipment.brand} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">{shipment.sku_count}</TableCell>
                      <TableCell align="right">{formatNumber(shipment.shipped)}</TableCell>
                      <TableCell align="right">{formatNumber(shipment.received)}</TableCell>
                      <TableCell align="right">
                        <Typography color="error.main" fontWeight="medium">
                          {formatNumber(shipment.total_missing)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${shipment.missing_pct}%`}
                          size="small"
                          color={getDiscrepancyColor(shipment.missing_pct)}
                        />
                      </TableCell>
                      <TableCell>
                        {shipment.has_actual_date ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                            <Typography variant="body2" color="success.main" fontWeight="medium">
                              {shipment.estimated_date}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            {shipment.estimated_date}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          onClick={() => handleViewShipment(shipment.shipment_id)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page}
            onPageChange={handlePageChange}
            rowsPerPage={pagination.rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </CardContent>
      </Card>

      {/* Shipment Detail Dialog */}
      <Dialog
        open={!!selectedShipment}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Shipment Details: {selectedShipment}
            </Typography>
            <IconButton onClick={handleCloseDetail} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : shipmentDetail ? (
            <Box>
              {/* Shipment Header Info */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Shipment Name</Typography>
                  <Typography variant="body1">{shipmentDetail.shipment_name || '—'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip label={shipmentDetail.shipment_status} size="small" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Destination FC</Typography>
                  <Typography variant="body1">{shipmentDetail.destination_fc || '—'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Estimated Date</Typography>
                  <Typography variant="body1">{shipmentDetail.estimated_date || '—'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Ship From</Typography>
                  <Typography variant="body1">
                    {[
                      shipmentDetail.ship_from_name,
                      shipmentDetail.ship_from_address,
                      shipmentDetail.ship_from_city,
                      shipmentDetail.ship_from_state,
                      shipmentDetail.ship_from_postal,
                      shipmentDetail.ship_from_country
                    ].filter(Boolean).join(', ') || '—'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Summary Stats */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="h4" color="primary.main">
                      {formatNumber(shipmentDetail.total_shipped)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Total Shipped</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="h4" color="success.main">
                      {formatNumber(shipmentDetail.total_received)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Total Received</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'error.50', borderRadius: 1 }}>
                    <Typography variant="h4" color="error.main">
                      {formatNumber(shipmentDetail.total_discrepancy)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Total Missing</Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Items Table */}
              <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                Items ({shipmentDetail.item_count})
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>SKU</TableCell>
                      <TableCell>ASIN</TableCell>
                      <TableCell align="right">Shipped</TableCell>
                      <TableCell align="right">Received</TableCell>
                      <TableCell align="right">Missing</TableCell>
                      <TableCell align="right">% Missing</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shipmentDetail.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            {item.seller_sku}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {item.asin || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatNumber(item.quantity_shipped)}</TableCell>
                        <TableCell align="right">{formatNumber(item.quantity_received)}</TableCell>
                        <TableCell align="right">
                          <Typography color="error.main" fontWeight="medium">
                            {formatNumber(item.discrepancy)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${item.discrepancy_pct}%`}
                            size="small"
                            color={getDiscrepancyColor(item.discrepancy_pct)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              Failed to load shipment details
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetail}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog
        open={csvDialogOpen}
        onClose={handleCloseCsvDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CloudUploadIcon color="primary" />
              <Typography variant="h6">Upload FBA Shipment Dates CSV</Typography>
            </Box>
            <IconButton onClick={handleCloseCsvDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* Stats */}
          {csvStats && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Current Database Stats
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Total Records</Typography>
                  <Typography variant="h6">{formatNumber(csvStats.total_records)}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Unique Shipments</Typography>
                  <Typography variant="h6">{formatNumber(csvStats.unique_shipments)}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Date Range</Typography>
                  <Typography variant="body1">
                    {csvStats.earliest_date || '—'} to {csvStats.latest_date || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Last Upload</Typography>
                  <Typography variant="body1">{csvStats.last_upload || '—'}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Message */}
          {csvMessage && (
            <Alert
              severity={csvMessage.type}
              sx={{ mb: 2 }}
              icon={csvMessage.type === 'success' ? <CheckCircleIcon /> : undefined}
            >
              {csvMessage.text}
            </Alert>
          )}

          {/* File Upload */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Select CSV File
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a CSV file containing shipment dates. Required columns: Shipment ID.
              Optional: Shipped Date, Expected Date, SKU, Name, etc.
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              disabled={csvUploading}
            >
              {csvFile ? csvFile.name : 'Choose CSV File'}
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={handleFileChange}
              />
            </Button>
          </Box>

          {/* Preview */}
          {csvUploading && !csvPreview && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
              <Typography sx={{ ml: 2 }}>Parsing CSV file...</Typography>
            </Box>
          )}

          {csvPreview && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Preview
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="h5" color="primary.main">
                      {formatNumber(csvPreview.totalRows)}
                    </Typography>
                    <Typography variant="body2">Total Rows</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="h5" color="success.main">
                      {formatNumber(csvPreview.validRows)}
                    </Typography>
                    <Typography variant="body2">Valid Rows</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="h5">
                      {formatNumber(csvPreview.skippedRows)}
                    </Typography>
                    <Typography variant="body2">Skipped</Typography>
                  </Box>
                </Grid>
              </Grid>

              {csvPreview.mappedColumns && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Detected columns: {csvPreview.mappedColumns.join(', ')}
                  </Typography>
                </Box>
              )}

              {csvPreview.preview && csvPreview.preview.length > 0 && (
                <TableContainer sx={{ maxHeight: 200 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Shipment ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Shipped Date</TableCell>
                        <TableCell>SKU</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {csvPreview.preview.slice(0, 5).map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            {row.Shipment_ID}
                          </TableCell>
                          <TableCell>
                            <Tooltip title={row.Name_ToFBA || ''}>
                              <Typography noWrap sx={{ maxWidth: 200 }}>
                                {row.Name_ToFBA || '—'}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>{row.Shipped_Date || '—'}</TableCell>
                          <TableCell>{row.Merchant_SKU || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCsvDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUploadCsv}
            disabled={!csvFile || !csvPreview || csvUploading}
            startIcon={csvUploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
          >
            {csvUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FbaShipmentDiscrepancies;
